import { createContext, useContext, useEffect, useState, useCallback } from "react";
import api from "@/lib/api";

const AuthContext = createContext(null);

export const useAuth = () => useContext(AuthContext);

export function AuthProvider({ children }) {
  const [loading, setLoading] = useState(true);
  const [user, setUser] = useState(null);
  const [tenants, setTenants] = useState([]);
  const [activeTenantId, setActiveTenantId] = useState(
    localStorage.getItem("active_tenant_id") || null
  );

  const applySession = useCallback((data) => {
    setUser(data.user);
    setTenants(data.tenants || []);
    const stored = localStorage.getItem("active_tenant_id");
    const valid = (data.tenants || []).some((t) => t.tenant_id === stored);
    if (!valid && data.tenants && data.tenants.length) {
      localStorage.setItem("active_tenant_id", data.tenants[0].tenant_id);
      setActiveTenantId(data.tenants[0].tenant_id);
    } else if (valid) {
      setActiveTenantId(stored);
    }
  }, []);

  const checkAuth = useCallback(async () => {
    try {
      const res = await api.get("/auth/me");
      applySession(res.data);
    } catch (e) {
      setUser(null);
      setTenants([]);
    } finally {
      setLoading(false);
    }
  }, [applySession]);

  useEffect(() => {
    // Si volvemos del callback de OAuth, dejamos que AuthCallback maneje la sesión.
    if (window.location.hash?.includes("session_id=")) {
      setLoading(false);
      return;
    }
    // Las páginas públicas (/, /l/:code, /g/:slug, /b/:slug, /privacy, /terms)
    // no tienen sesión ni la necesitan: evitamos el 401 innecesario en la
    // consola en cada visita. OJO: "/" se compara EXACTO, nunca con
    // startsWith -- "/".startsWith("/") es true para CUALQUIER ruta y
    // desactivaría la sesión en todo el panel.
    const PUBLIC_PREFIXES = ["/l/", "/g/", "/b/", "/privacy", "/terms"];
    const path = window.location.pathname;
    if (path === "/" || PUBLIC_PREFIXES.some((p) => path.startsWith(p))) {
      setLoading(false);
      return;
    }
    checkAuth();
  }, [checkAuth]);

  const setActiveTenant = (tenantId) => {
    localStorage.setItem("active_tenant_id", tenantId);
    setActiveTenantId(tenantId);
  };

  const logout = async () => {
    try { await api.post("/auth/logout"); } catch (e) {}
    localStorage.removeItem("active_tenant_id");
    setUser(null);
    setTenants([]);
    setActiveTenantId(null);
    window.location.href = "/login";
  };

  const activeTenant = tenants.find((t) => t.tenant_id === activeTenantId) || null;
  const role = activeTenant?.role || null;

  return (
    <AuthContext.Provider
      value={{ loading, user, tenants, activeTenantId, activeTenant, role,
               setActiveTenant, applySession, checkAuth, logout }}
    >
      {children}
    </AuthContext.Provider>
  );
}
